class CustomPagination extends HTMLElement {
  constructor() {
    super();
    this.initializeProperties();
  }

  initializeProperties() {
    this.sectionId = this.dataset.section;
    this.productList = document.querySelector(`ul[data-id="${this.sectionId}"]`);
    this.loadMoreButton = this.querySelector('.btn--load-more');
    this.paginationType = this.getAttribute('data-type');
    this.countItem = parseInt(this.productList.getAttribute('data-products-count'), 10);
    this.currentPage = 2;
    this.scrollObserver = null;
  }

  connectedCallback() {
    if (this.paginationType === 'load-more-button') {
      this.setupLoadMoreButton();
    } else if (this.paginationType === 'infinite-scroll') {
      this.setupInfiniteScroll();
    }
  }

  setupLoadMoreButton() {
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', () => this.loadNextPage());
    }
  }

  setupInfiniteScroll() {
    this.scrollObserver = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) this.loadNextPage();
      }, 
      { threshold: [0, 1] }
    );
    this.scrollObserver.observe(this);
  }

  buildUrlWithPageNumber(search, key) {
    const urlParams = new URLSearchParams(search || '');
    urlParams.set(key, this.currentPage);
    return `?${urlParams.toString()}`;
  }

  async loadNextPage() {
    if (this.hasAttribute('loading')) return;

    try {
      this.setAttribute('loading', true);
      this.showLoadingSpinner();
      
      const url = `${document.location.pathname}${this.buildUrlWithPageNumber(document.location.search, 'page')}`;
      const response = await fetch(url);
      const html = await response.text();
      
      this.renderFetchedProducts(html);
      this.currentPage++;
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      this.hideLoadingSpinner();
      this.removeAttribute('loading');
    }
  }

  renderFetchedProducts(html) {
    const parser = new DOMParser();
    const newDocument = parser.parseFromString(html, 'text/html');
    const newProductGrid = newDocument.getElementById('product-grid');
    
    if (!newProductGrid || !newProductGrid.children.length) {
      this.finalizePagination();
      return;
    }

    const newProducts = newProductGrid.querySelectorAll('.grid__item');
    newProducts.forEach(product => {
      // Mark products as dynamically loaded before appending
      product.setAttribute('data-pagination-loaded', 'true');
      this.productList.appendChild(product);
    });

    // Small delay to ensure DOM is ready
    requestAnimationFrame(() => {
      // Initialize ResponsiveImage elements for newly loaded products
      this.initializeResponsiveImages();

      // Initialize any product card carousels for the new products
      this.initializeProductCarousels();
    });

    if (this.productList.children.length >= this.countItem) {
      this.finalizePagination();
    }
  }

  showLoadingSpinner() {
    if (!this.loadMoreButton) return;
    
    this.loadMoreButton.classList.add('loading');
    const spinner = this.loadMoreButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
  }

  hideLoadingSpinner() {
    if (!this.loadMoreButton) return;
    
    this.loadMoreButton.classList.remove('loading');
    const spinner = this.loadMoreButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.add('hidden');
  }

  initializeResponsiveImages() {
    // Find all newly added responsive-image elements
    const newResponsiveImages = this.productList.querySelectorAll('responsive-image:not([data-pagination-initialized])');
    
    newResponsiveImages.forEach(responsiveImage => {
      // Mark as initialized by pagination
      responsiveImage.setAttribute('data-pagination-initialized', 'true');
      
      const img = responsiveImage.querySelector('img');
      if (img) {
        // Check if the image has srcset stored in data attribute
        const originalSrcset = img.getAttribute('data-srcset') || img.getAttribute('srcset');
        
        if (originalSrcset && !img.getAttribute('srcset')) {
          // If srcset was removed by ResponsiveImage class, add it back
          img.setAttribute('srcset', originalSrcset);
        }
        
        // Ensure the image loads properly
        if (img.classList.contains('img-loading')) {
          img.classList.remove('img-loading');
          img.classList.add('img-loaded');
        }
        
        // Update sizes if needed
        if (responsiveImage.hasAttribute('data-sizes') && responsiveImage.getAttribute('data-sizes') === 'true') {
          const width = Math.floor(img.getBoundingClientRect().width);
          if (width > 0) {
            img.setAttribute('sizes', `${width}px`);
          }
        }
      }
    });

    // Dispatch event to notify that new products have been loaded
    document.dispatchEvent(new CustomEvent('pagination:products-loaded', {
      detail: { container: this.productList }
    }));
  }

  initializeProductCarousels() {
    // Initialize Splide carousels for new product cards
    const newCarousels = this.productList.querySelectorAll('.card-product-carousel:not([data-carousel-initialized])');
    
    newCarousels.forEach(carousel => {
      // Mark as initialized
      carousel.setAttribute('data-carousel-initialized', 'true');
      
      // Initialize Splide if it's available
      if (typeof Splide !== 'undefined') {
        const carouselOptions = JSON.parse(carousel.getAttribute('data-splide') || '{}');
        new Splide(carousel, carouselOptions).mount();
      }
    });
  }

  finalizePagination() {
    if (this.scrollObserver) {
      this.scrollObserver.unobserve(this);
      this.scrollObserver.disconnect();
    }
    
    if (this.loadMoreButton) {
      this.loadMoreButton.classList.add('visually-hidden');
    }
  }

  disconnectedCallback() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
    
    if (this.loadMoreButton) {
      this.loadMoreButton.removeEventListener('click', this.loadNextPage);
    }
  }
}

if (!customElements.get('custom-pagination')) {
  customElements.define('custom-pagination', CustomPagination);
}